import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Badge, Button, Collapse, Input, Label } from "reactstrap";
import { Filter, Search, X } from "react-feather";
import { operationLabel } from "./glUtils";

const splitList = (value) => (value ? String(value).split(",").filter(Boolean) : []);

const toOptions = (items, getValue, getLabel) =>
  (items || []).map((item) => ({ value: String(getValue(item)), label: getLabel(item) }));

const pick = (options, values) => options.filter((o) => values.includes(o.value));

const TRI_STATE = [
  { value: "", label: "Any" },
  { value: "1", label: "Yes" },
  { value: "0", label: "No" },
];

// The menu is portaled to <body> so the table's sticky header and scroll container can't clip it.
const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", minWidth: 200 }),
};

const PRIMARY_KEYS = ["q", "company_id", "bank_profile_id", "currency", "date_from", "date_to"];
const ADVANCED_KEYS = ["type", "amount_min", "amount_max", "operation_type", "has_note", "has_attachment", "needs_review", "category"];

export default function LedgerFilters({ meta, filters, onChange, onReset }) {
  const [search, setSearch] = useState(filters.q || "");
  const advancedCount = ADVANCED_KEYS.filter((key) => filters[key]).length;
  const [showAdvanced, setShowAdvanced] = useState(advancedCount > 0);
  const hasAnyFilter = [...PRIMARY_KEYS, ...ADVANCED_KEYS, "statement_id"].some((key) => filters[key]);

  useEffect(() => setSearch(filters.q || ""), [filters.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if ((filters.q || "") !== search.trim()) onChange({ q: search.trim() });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [search]);

  const selectedCompanies = splitList(filters.company_id);
  const selectedCompaniesKey = selectedCompanies.join(",");

  const companyOptions = useMemo(
    () => toOptions(meta?.companies, (c) => c.id, (c) => c.name),
    [meta]
  );
  const profileOptions = useMemo(
    () =>
      toOptions(
        (meta?.bank_profiles || []).filter(
          (p) => !selectedCompaniesKey || selectedCompaniesKey.split(",").includes(String(p.company_id))
        ),
        (p) => p.id,
        (p) => `${p.label || p.bank_name} ****${p.account_last4 || ""} (${p.currency_code})`
      ),
    [meta, selectedCompaniesKey]
  );
  const currencyOptions = useMemo(
    () => toOptions(meta?.currencies, (c) => c, (c) => c),
    [meta]
  );
  const typeOptions = useMemo(
    () => toOptions(meta?.operation_types, (t) => t, (t) => operationLabel(t)),
    [meta]
  );

  const multi = (key, options, placeholder) => (
    <Select
      isMulti
      classNamePrefix="gl-select"
      className="gl-select"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={SELECT_STYLES}
      placeholder={placeholder}
      options={options}
      value={pick(options, splitList(filters[key]))}
      onChange={(selected) => onChange(patchFor(key, (selected || []).map((s) => s.value)))}
    />
  );

  // An account filter outside the selected companies would be invisible but still applied.
  const patchFor = (key, values) => {
    const patch = { [key]: values.join(",") };
    if (key === "company_id" && values.length > 0 && filters.bank_profile_id) {
      const allowed = new Set(
        (meta?.bank_profiles || []).filter((p) => values.includes(String(p.company_id))).map((p) => String(p.id))
      );
      patch.bank_profile_id = splitList(filters.bank_profile_id).filter((id) => allowed.has(id)).join(",");
    }
    return patch;
  };

  return (
    <div className="gl-filters">
      <div className="gl-filter-bar">
        <div className="gl-search">
          <Search size={15} />
          <Input
            type="search"
            placeholder="Search movements…"
            title="Search by label, counterparty or reference"
            value={search}
            maxLength={100}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="gl-date-range">
          <Input
            type="date"
            aria-label="From"
            title="From date"
            className={filters.date_from ? "" : "gl-date-empty"}
            value={filters.date_from || ""}
            onChange={(e) => onChange({ date_from: e.target.value })}
          />
          <span className="gl-muted">→</span>
          <Input
            type="date"
            aria-label="To"
            title="To date"
            className={filters.date_to ? "" : "gl-date-empty"}
            value={filters.date_to || ""}
            onChange={(e) => onChange({ date_to: e.target.value })}
          />
        </div>
        <div className="gl-filter-select">{multi("company_id", companyOptions, "All companies")}</div>
        <div className="gl-filter-select">{multi("bank_profile_id", profileOptions, "All accounts")}</div>
        <div className="gl-filter-select gl-filter-select-sm">{multi("currency", currencyOptions, "Currency")}</div>
        <div className="gl-filter-actions">
          <Button
            color={showAdvanced ? "primary" : "light"}
            outline={showAdvanced}
            className="d-inline-flex align-items-center gap-1"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <Filter size={14} /> More
            {advancedCount > 0 && <Badge color="primary" pill>{advancedCount}</Badge>}
          </Button>
          {hasAnyFilter && (
            <Button color="link" className="gl-reset d-inline-flex align-items-center gap-1" onClick={onReset}>
              <X size={14} /> Clear
            </Button>
          )}
        </div>
      </div>

      <Collapse isOpen={showAdvanced}>
        <div className="gl-filter-advanced">
          <div>
            <Label className="gl-filter-label">Direction</Label>
            <Input type="select" bsSize="sm" value={filters.type || ""} onChange={(e) => onChange({ type: e.target.value })}>
              <option value="">Debit & credit</option>
              <option value="debit">Debit only</option>
              <option value="credit">Credit only</option>
            </Input>
          </div>
          <div>
            <Label className="gl-filter-label">Amount</Label>
            <div className="d-flex gap-1">
              <Input
                type="number"
                bsSize="sm"
                min="0"
                step="any"
                placeholder="Min"
                value={filters.amount_min || ""}
                onChange={(e) => onChange({ amount_min: e.target.value })}
              />
              <Input
                type="number"
                bsSize="sm"
                min="0"
                step="any"
                placeholder="Max"
                value={filters.amount_max || ""}
                onChange={(e) => onChange({ amount_max: e.target.value })}
              />
            </div>
          </div>
          <div className="gl-filter-wide">
            <Label className="gl-filter-label">Operation type</Label>
            {multi("operation_type", typeOptions, "All types")}
          </div>
          <div>
            <Label className="gl-filter-label">Category</Label>
            <Input type="select" bsSize="sm" value={filters.category || ""} onChange={(e) => onChange({ category: e.target.value })}>
              <option value="">Any category</option>
              {(meta?.categories || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Input>
          </div>
          {[
            ["has_note", "Has note"],
            ["has_attachment", "Has attachment"],
            ["needs_review", "Needs review"],
          ].map(([key, label]) => (
            <div key={key}>
              <Label className="gl-filter-label">{label}</Label>
              <Input type="select" bsSize="sm" value={filters[key] ?? ""} onChange={(e) => onChange({ [key]: e.target.value })}>
                {TRI_STATE.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Input>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
}
